import React, { useEffect, useRef, useState } from "react";
import { DndContext, closestCorners, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

const Handle = ({ listeners, attributes, label, testid }) => (
  <button type="button" {...attributes} {...listeners} aria-label={label} title={label} data-testid={testid}
    className="text-zinc-500 hover:text-white cursor-grab active:cursor-grabbing touch-none shrink-0 p-1 -ml-1 rounded hover:bg-white/5">
    <GripVertical size={18} />
  </button>
);

// Let the whole row start a drag, except when pressing a button, link or text box.
const NO_SELECT = { userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none" };
const rowListeners = (listeners) => {
  const skip = (e) => e.target.closest("button, a, input, textarea, select, [contenteditable]");
  return {
    onPointerDown: (e) => { if (!skip(e)) listeners?.onPointerDown?.(e); },
    onTouchStart: (e) => { if (!skip(e)) listeners?.onTouchStart?.(e); },
  };
};

const useSortStyle = (id) => {
  const s = useSortable({ id });
  const style = { transform: CSS.Transform.toString(s.transform), transition: s.transition, opacity: s.isDragging ? 0.5 : 1, position: "relative", zIndex: s.isDragging ? 20 : "auto" };
  return { ...s, style };
};

const SortableItem = ({ it, ii, iArr, renderItem }) => {
  const { setNodeRef, style, listeners, attributes } = useSortStyle(`i:${it.id}`);
  return (
    <div ref={setNodeRef} style={{ ...style, ...NO_SELECT }} className="cursor-grab active:cursor-grabbing" {...rowListeners(listeners)}>
      {renderItem(it, ii, iArr, <Handle listeners={listeners} attributes={attributes} label="Drag to move this lesson" testid={`beacon-item-drag-${it.id}`} />)}
    </div>
  );
};

const SortableSection = ({ s, si, sArr, renderHeader, renderItem, renderFooter }) => {
  const { setNodeRef, style, listeners, attributes, isOver } = useSortStyle(`s:${s.id}`);
  return (
    <div ref={setNodeRef} style={style} className={`mb-5 border p-4 bg-[#121212] transition-colors ${isOver ? "border-[#14A89A]/50" : "border-white/8"}`} data-testid={`beacon-section-${s.id}`}>
      <div style={NO_SELECT} className="cursor-grab active:cursor-grabbing" {...rowListeners(listeners)} data-testid={`beacon-section-header-${s.id}`}>
        {renderHeader(s, si, sArr, <Handle listeners={listeners} attributes={attributes} label="Drag to move this section" testid={`beacon-section-drag-${s.id}`} />)}
      </div>
      <SortableContext items={s.items.map((it) => `i:${it.id}`)} strategy={verticalListSortingStrategy}>
        {s.items.length === 0 && <p className="font-body text-xs text-zinc-600 py-2 border-t border-white/5">Drop a lesson here</p>}
        {s.items.map((it, ii, iArr) => <SortableItem key={it.id} it={it} ii={ii} iArr={iArr} renderItem={renderItem} />)}
      </SortableContext>
      {renderFooter(s)}
    </div>
  );
};

const shape = (secs) => JSON.stringify(secs.map((s) => [s.id, s.items.map((it) => it.id)]));

// Drag-and-drop board: reorder sections, and move lessons within or between sections.
export const BeaconContentBoard = ({ sections, onReorder, renderHeader, renderItem, renderFooter }) => {
  const [layout, setLayout] = useState(sections);
  const ref = useRef(sections);
  const set = (next) => { ref.current = next; setLayout(next); };
  useEffect(() => { set(sections); }, [sections]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const secOf = (id, secs = ref.current) => (id.startsWith("s:") ? secs.findIndex((s) => `s:${s.id}` === id) : secs.findIndex((s) => s.items.some((it) => `i:${it.id}` === id)));

  const onDragOver = ({ active, over }) => {
    if (!over || !active.id.startsWith("i:")) return;
    const secs = ref.current;
    const from = secOf(active.id), to = secOf(over.id);
    if (from < 0 || to < 0 || from === to) return;
    const item = secs[from].items.find((it) => `i:${it.id}` === active.id);
    const target = secs[to].items;
    const overIdx = over.id.startsWith("i:") ? target.findIndex((it) => `i:${it.id}` === over.id) : target.length;
    const next = secs.map((s, i) => {
      if (i === from) return { ...s, items: s.items.filter((it) => it.id !== item.id) };
      if (i === to) { const items = [...s.items]; items.splice(overIdx < 0 ? items.length : overIdx, 0, item); return { ...s, items }; }
      return s;
    });
    set(next);
  };

  const onDragEnd = ({ active, over }) => {
    let next = ref.current;
    if (over) {
      if (active.id.startsWith("s:")) {
        const a = secOf(active.id), b = secOf(over.id);
        if (a >= 0 && b >= 0 && a !== b) next = arrayMove(next, a, b);
      } else if (over.id.startsWith("i:")) {
        const si = secOf(active.id);
        if (si >= 0 && si === secOf(over.id)) {
          const items = next[si].items;
          const a = items.findIndex((it) => `i:${it.id}` === active.id), b = items.findIndex((it) => `i:${it.id}` === over.id);
          if (a !== b) next = next.map((s, i) => (i === si ? { ...s, items: arrayMove(items, a, b) } : s));
        }
      }
    }
    set(next);
    if (shape(next) !== shape(sections)) onReorder(next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={() => set(sections)}>
      <SortableContext items={layout.map((s) => `s:${s.id}`)} strategy={verticalListSortingStrategy}>
        {layout.map((s, si, sArr) => (
          <SortableSection key={s.id} s={s} si={si} sArr={sArr} renderHeader={renderHeader} renderItem={renderItem} renderFooter={renderFooter} />
        ))}
      </SortableContext>
    </DndContext>
  );
};
